import { type KmAttrType, KM_ATTR_DATA_TYPES } from '@/entities/ontology';

/** Строка ввода: код атрибута и его значение в форме */
export type KmAttrFormRow = {
  code: string;
  value: KmAttrFormValue;
};

/**
 * Значение атрибута в форме: скаляр набирается строкой (логический — признаком),
 * список — массивом значений, структура — строками своих полей.
 *
 * Строки вместо чисел и дат нужны, чтобы недобранное значение («-», «12.») оставалось
 * в поле до сохранения: приведение к числу происходит один раз, при разборе.
 */
export type KmAttrFormValue = string | boolean | KmAttrFormValue[] | KmAttrFormRow[];

/** Причина, по которой набранное значение не удалось привести к типу атрибута */
export type KmAttrValueError = 'integer' | 'number' | 'json';

/** Разбор набранного значения: либо значение для отправки, либо причина отказа */
export type KmAttrParseResult = { value: unknown; error?: undefined } | { value?: undefined; error: KmAttrValueError };

/** Поиск атрибута справочника по коду — нужен полям структуры */
export type KmAttrTypeResolver = (code: string) => KmAttrType | undefined;

/**
 * Значение поля формы изменено. Признак приходит от формы вложенным: у набора
 * и структуры — по признаку на каждую часть значения.
 */
export const isKmFormFieldDirty = (dirty: unknown): boolean => {
  if (Array.isArray(dirty)) {
    return dirty.some(isKmFormFieldDirty);
  }
  if (dirty !== null && typeof dirty === 'object') {
    return Object.values(dirty).some(isKmFormFieldDirty);
  }
  return dirty === true;
};

/** Значение атрибута — набор элементов: признак списка либо множественный выбор */
export const isKmAttrList = (attrType: KmAttrType): boolean =>
  attrType.isList || attrType.dataType === KM_ATTR_DATA_TYPES.MULTISELECT;

/**
 * Значение выбирается из справочника экземпляров, а не из встроенного перечня вариантов.
 * Справочник осмыслен только у выбора: у прочих типов бэкенд `refEntityType` не смотрит и
 * ждёт значение своего типа, поэтому идентификатора экземпляра там не бывает.
 */
export const isKmAttrRef = (attrType: KmAttrType): boolean =>
  (attrType.dataType === KM_ATTR_DATA_TYPES.SELECT || attrType.dataType === KM_ATTR_DATA_TYPES.MULTISELECT) &&
  Boolean(attrType.options?.refEntityType) &&
  !attrType.options?.choices?.length;

/**
 * Значение набирается записью JSON: у произвольного значения состава нет, а набор структур
 * полями развернуть не во что — у элементов набора нет ни своих подписей, ни порядка.
 */
export const isKmAttrJsonInput = (attrType: KmAttrType): boolean =>
  attrType.dataType === KM_ATTR_DATA_TYPES.JSON ||
  (isKmAttrList(attrType) && attrType.dataType === KM_ATTR_DATA_TYPES.STRUCT);

/** Поля структуры в порядке отображения */
export const getKmStructFields = (attrType: KmAttrType) =>
  [...(attrType.options?.fields ?? [])].sort((a, b) => a.displayOrder - b.displayOrder);

const toJsonText = (value: unknown): string =>
  value === null || value === undefined ? '' : JSON.stringify(value, null, 2);

const scalarToFormValue = (
  attrType: KmAttrType,
  value: unknown,
  resolveAttrType: KmAttrTypeResolver,
): KmAttrFormValue => {
  if (attrType.dataType === KM_ATTR_DATA_TYPES.BOOL) {
    return value === true;
  }
  if (attrType.dataType === KM_ATTR_DATA_TYPES.STRUCT) {
    const fieldValues = value !== null && typeof value === 'object' ? (value as Record<string, unknown>) : {};
    return getKmStructFields(attrType).map<KmAttrFormRow>((field) => {
      const fieldAttrType = resolveAttrType(field.attrCode);
      return {
        code: field.attrCode,
        value: fieldAttrType ? toKmAttrFormValue(fieldAttrType, fieldValues[field.attrCode], resolveAttrType) : '',
      };
    });
  }
  return value === null || value === undefined ? '' : String(value);
};

/**
 * Значение экземпляра — в значение поля формы по типу атрибута.
 */
export const toKmAttrFormValue = (
  attrType: KmAttrType,
  value: unknown,
  resolveAttrType: KmAttrTypeResolver,
): KmAttrFormValue => {
  if (isKmAttrJsonInput(attrType)) {
    return toJsonText(value);
  }
  if (isKmAttrList(attrType)) {
    const items = Array.isArray(value) ? value : [];
    return items.map((item) => scalarToFormValue(attrType, item, resolveAttrType));
  }
  return scalarToFormValue(attrType, value, resolveAttrType);
};

const parseScalar = (
  attrType: KmAttrType,
  formValue: unknown,
  resolveAttrType: KmAttrTypeResolver,
): KmAttrParseResult => {
  if (attrType.dataType === KM_ATTR_DATA_TYPES.BOOL) {
    return { value: formValue === true };
  }

  if (attrType.dataType === KM_ATTR_DATA_TYPES.STRUCT) {
    const rows = Array.isArray(formValue) ? (formValue as KmAttrFormRow[]) : [];
    const struct: Record<string, unknown> = {};

    for (const row of rows) {
      const fieldAttrType = resolveAttrType(row.code);
      if (!fieldAttrType) {
        continue;
      }
      const parsed = parseKmAttrValue(fieldAttrType, row.value, resolveAttrType);
      if (parsed.error) {
        return parsed;
      }
      // Незаполненное поле структуры не отправляется: иначе обязательное поле
      // отказывалось бы по пустому значению вместо его отсутствия
      if (parsed.value !== null) {
        struct[row.code] = parsed.value;
      }
    }

    return { value: Object.keys(struct).length ? struct : null };
  }

  const text = typeof formValue === 'string' ? formValue.trim() : '';
  if (!text) {
    return { value: null };
  }

  if (attrType.dataType === KM_ATTR_DATA_TYPES.INT) {
    const parsed = Number(text);
    return Number.isInteger(parsed) ? { value: parsed } : { error: 'integer' };
  }

  if (attrType.dataType === KM_ATTR_DATA_TYPES.DECIMAL) {
    const parsed = Number(text);
    return Number.isFinite(parsed) ? { value: parsed } : { error: 'number' };
  }

  // Справочник экземпляров адресуется идентификатором, встроенный перечень — кодом варианта
  if (isKmAttrRef(attrType)) {
    const parsed = Number(text);
    return Number.isInteger(parsed) ? { value: parsed } : { error: 'integer' };
  }

  return { value: text };
};

/**
 * Набранное значение — в значение экземпляра по типу атрибута. Пустое значение и пустой
 * набор дают `null`: у экземпляра такого значения нет.
 */
export const parseKmAttrValue = (
  attrType: KmAttrType,
  formValue: unknown,
  resolveAttrType: KmAttrTypeResolver,
): KmAttrParseResult => {
  if (isKmAttrJsonInput(attrType)) {
    const text = typeof formValue === 'string' ? formValue.trim() : '';
    if (!text) {
      return { value: null };
    }
    try {
      return { value: JSON.parse(text) };
    } catch {
      return { error: 'json' };
    }
  }

  if (!isKmAttrList(attrType)) {
    return parseScalar(attrType, formValue, resolveAttrType);
  }

  const items: unknown[] = Array.isArray(formValue) ? formValue : [];
  const parsedItems: unknown[] = [];

  for (const item of items) {
    const parsed = parseScalar(attrType, item, resolveAttrType);
    if (parsed.error) {
      return parsed;
    }
    // Пустой элемент набора значением не является и в список не попадает
    if (parsed.value !== null) {
      parsedItems.push(parsed.value);
    }
  }

  return { value: parsedItems.length ? parsedItems : null };
};
